// domains/PostCreator/storiesStageConfig.ts
//
// Trimmed stage config for the Stories board. Mirrors the entry shape of
// `utils/stageConfig.ts` STAGE_CONFIGS, but stories only move through a small
// subset of the post lifecycle: they are auto-populated as drafts from
// approved story media, scheduled, published, then archived.
//
// Reuses `PostStatus` values and component icons from `@/components/icons`
// (no inline SVG, no emoji glyphs).
import React from "react";
import { PostStatus } from "@/core/types/postTypes";
import {
  DocumentIcon,
  CalendarCheckIcon,
  CheckIcon,
  EyeIcon,
  TrashIcon,
  IconProps,
} from "@/components/icons";

// The Stories board exposes exactly these five stages.
export type StoryStage =
  | "draft"
  | "approval"
  | "scheduled"
  | "published"
  | "discarded";

export interface StoryStageConfig {
  id: StoryStage;
  label: string;
  description: string;
  statuses: PostStatus[];
  color: string;
  bgColor: string;
  textColor: string;
  // Component icon (from @/components/icons), rendered by the board. Kept as a
  // component type rather than an emoji string so we never fall back to inline
  // SVG in the consuming view.
  icon: React.ComponentType<IconProps>;
}

export const STORIES_STAGE_CONFIGS: StoryStageConfig[] = [
  {
    id: "draft",
    label: "Bozza",
    description: "Storie in fase di stesura",
    // READY lives here too: scheduling/publishing promotes DRAFT → READY
    // before the modal opens, and a status with no stage bucket would unmount
    // the card mid-flow (killing the modal) and strand the story off-board.
    statuses: [PostStatus.DRAFT, PostStatus.READY],
    color: "slate",
    bgColor: "bg-slate-50",
    textColor: "text-slate-700",
    icon: DocumentIcon,
  },
  {
    // Client-approval stage. Mirrors the feed posts' `approval_pending` stage
    // (utils/stageConfig.ts): label "In attesa", amber palette. A story at
    // APPROVAL_PENDING already surfaces in the client portal server-side.
    id: "approval",
    label: "In attesa",
    description: "Storie in attesa di approvazione del cliente",
    statuses: [PostStatus.APPROVAL_PENDING],
    color: "amber",
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
    icon: EyeIcon,
  },
  {
    id: "scheduled",
    label: "Programmato",
    description: "Storie programmate per la pubblicazione",
    statuses: [PostStatus.SCHEDULED],
    color: "purple",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    icon: CalendarCheckIcon,
  },
  {
    id: "published",
    label: "Pubblicato",
    description: "Storie pubblicate",
    statuses: [PostStatus.PUBLISHED, PostStatus.PARTIALLY_PUBLISHED],
    color: "emerald",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
    icon: CheckIcon,
  },
  {
    id: "discarded",
    label: "Scartato",
    description: "Storie archiviate",
    statuses: [PostStatus.ARCHIVED],
    color: "gray",
    bgColor: "bg-gray-50",
    textColor: "text-gray-700",
    icon: TrashIcon,
  },
];

export const getStoryStageConfig = (stage: StoryStage): StoryStageConfig => {
  return (
    STORIES_STAGE_CONFIGS.find((config) => config.id === stage) ||
    STORIES_STAGE_CONFIGS[0]
  );
};

// Board sort: newest story first, on every tab including Tutti. Cards carry
// no stage grouping visually, so recency is the only ordering that reads as
// ordered. Lives here (not in useStories) so it stays jest-testable — the
// hook module pulls axios/ESM and breaks jest.
export const compareStoriesForBoard = (
  a: { createdAt: string },
  b: { createdAt: string },
): number =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
